"use client";

import { useState, useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Leaf, MapPin, Buildings, CurrencyInr } from "@phosphor-icons/react/dist/ssr";
import CuratedList from "@/components/Lists/CuratedList";

type CuratedCollection = {
  title: string;
  location: string;
  image_url: string | null;
  starting_price: string;
  description: string;
  slug: string;
  icon: "building" | "house";
  category: "location" | "lifestyle" | "builder" | "budget";
  projects?: any[];
};

type CategoryInfo = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

type CategorizedCollections = {
  location: CuratedCollection[];
  lifestyle: CuratedCollection[];
  builder: CuratedCollection[];
  budget: CuratedCollection[];
};

type ClientTabsProps = {
  city: string;
  defaultCategory: string;
  categorizedCollections: CategorizedCollections;
  categoryInfo: Record<string, CategoryInfo>;
};

function Indicator() {
    return (
        <div>
            <span className="indicator-item status status-accent animate-ping"></span>
            <span className="indicator-item status status-accent"></span>
        </div>
    )
}


export default function ClientTabs({ 
  city,
  defaultCategory,
  categorizedCollections,
  categoryInfo
}: ClientTabsProps) {
  // Use state to manage the active tab
  const [activeCategory, setActiveCategory] = useState<string>(defaultCategory);
  const [showTabIndicator, setShowTabIndicator] = useState<boolean>(false);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  
  // Track visits using localStorage
  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      try {
        // Get current visit count from localStorage
        const storageKey = `${city}-tabs-visit-count`;
        let visitCount = parseInt(localStorage.getItem(storageKey) || '0', 10);
        
        // Increment visit count
        visitCount += 1;
        
        // Save updated count
        localStorage.setItem(storageKey, visitCount.toString());
        
        // Show indicator for first 5 visits
        setShowTabIndicator(visitCount <= 5);
      } catch (error) {
        // In case of private browsing or other localStorage issues
        console.error('Error accessing localStorage:', error);
        // Default to showing the indicator
        setShowTabIndicator(true);
      }
    }
  }, [city]);
  
  // Get collections for active category
  const activeCollections = 
    activeCategory === "lifestyle" ? categorizedCollections.lifestyle :
    activeCategory === "builder" ? categorizedCollections.builder :
    activeCategory === "budget" ? categorizedCollections.budget :
    categorizedCollections.location;
  
  // Handle tab click - update state and URL (without page reload)
  const handleTabClick = (category: string) => {
    setActiveCategory(category);
    
    // Update URL for bookmarking purposes, but don't navigate
    const params = new URLSearchParams(searchParams?.toString() || "");
    params.set("category", category);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };
  
  return (
    <>
      {/* Sticky Tabs Navigation */}
      <div className="sticky top-0 z-10 bg-white py-2 relative">
        <div className="mx-4 md:mx-auto md:max-w-6xl">
          <div className="tabs tabs-lift tabs-lg md:tabs-lg w-full">
            <div className={`tab indicator ${activeCategory == "location" ? "tab-active text-primary border-primary font-medium" : ""}  indicator-center md:min-w-48 `}>
              {showTabIndicator && activeCategory !== "location" && (
                <Indicator />
              )}
              <button 
                onClick={() => handleTabClick("location")}
              >
                <MapPin className="w-4 h-4 mr-1 inline-block" weight="fill" />
                Location
              </button>
            </div>
            
            <div className={`tab indicator ${activeCategory == "lifestyle" ? "tab-active text-primary border-primary font-medium" : ""}  indicator-center md:min-w-48 `}>
              {showTabIndicator && activeCategory !== "lifestyle" && (
                <Indicator />
              )}
              <button 
                onClick={() => handleTabClick("lifestyle")}
              >
                <Leaf className="w-4 h-4 mr-1 inline-block" weight="fill" />
                Lifestyle
              </button>
            </div>
            
            <div className={`tab indicator ${activeCategory == "builder" ? "tab-active text-primary border-primary font-medium" : ""}  indicator-center md:min-w-48 `}>
              {showTabIndicator && activeCategory !== "builder" && (
                <Indicator />
              )}
              <button 
                onClick={() => handleTabClick("builder")}
              >
                <Buildings className="w-4 h-4 mr-1 inline-block" weight="fill" />
                Builder
              </button>
            </div>
            
            <div className={`tab indicator ${activeCategory == "budget" ? "tab-active text-primary border-primary font-medium" : ""}  indicator-center md:min-w-48 `}>
              {showTabIndicator && activeCategory !== "budget" && (
                <Indicator />
              )}
              <button 
                onClick={() => handleTabClick("budget")}
              >
                <CurrencyInr className="w-4 h-4 mr-1 inline-block" weight="fill" />
                Budget
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-4 md:mx-auto md:max-w-6xl pt-10">
        {/* Active Collection */}
        <div className="mb-16">
          <div className="flex items-center gap-2 mb-4">
            {categoryInfo[activeCategory].icon}
            <h3 className="text-3xl font-semibold">{categoryInfo[activeCategory].title}</h3>
          </div>
          <p className="text-gray-600 mb-8 max-w-3xl">
            {categoryInfo[activeCategory].description}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-4">
            {activeCollections.map((collection: CuratedCollection) => (
              <div key={collection.slug} className="w-full">
                <CuratedList
                  title={collection.title}
                  location={collection.location}
                  imageUrl={
                    collection.image_url ||
                    "/placeholder.svg?height=400&width=600"
                  }
                  propertyCount={collection.projects?.length || 0}
                  startingPrice={collection.starting_price}
                  description={collection.description}
                  linkUrl={collection.slug}
                  icon={collection.icon}
                  category={collection.category}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
} 